import {
  Box,
  Card,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  Typography,
  useMediaQuery
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import ChatRoundedIcon from "@material-ui/icons/ChatRounded";
import { Link as RouterLink } from "react-router-dom";
import useWhatsappNumbers from "hooks/useWhatsappNumbers";
import { useWebotStatus } from "hooks/useWebotStatus";
import { PATH_DASHBOARD } from "routes/paths";
import Label from "components/Label";

export type WhatsappSendChannel = "official" | "extension";

/**
 * Show the official option to accounts WITHOUT the officialWhatsapp feature, disabled, as an
 * upsell. Flip to false and such an account skips the chooser entirely (see `visibleOptions`).
 *
 * The trade-off, stated so it stays a product decision rather than an accident: "ask support" only
 * works as a sentence when the thing being asked about is on screen, which is what was asked for —
 * but for an account that will never buy it, this is a permanently dead row riding every send.
 */
const SHOW_OFFICIAL_WHEN_UNLICENSED = true;

type Props = {
  open: boolean;
  onClose: () => void;
  onChoose: (channel: WhatsappSendChannel) => void;
  recipientCount: number;
  /** Whether the account has the officialWhatsapp feature at all (a licence, NOT a capability). */
  hasOfficialFeature: boolean;
  /**
   * The snapshot gate the EXTENSION sender alone is subject to: it reads `row.contact` for skip
   * flags and template variables, while the official send only needs the phone.
   */
  extensionDisabledReason?: string;
  /** Which channel was used last, pre-selected and badged. */
  lastChannel?: WhatsappSendChannel | "";
  /**
   * What the official card promises. Defaults to the listing share's ("the listing is added to each
   * customer's minisite"), which is only true there — the CRM's bulk send passes its own.
   */
  officialDescription?: string;
};

type Option = {
  channel: WhatsappSendChannel;
  visible: boolean;
  title: string;
  description: string;
  icon: JSX.Element;
  /** Still resolving — render a spinner, never a "not connected" claim. */
  probing: boolean;
  disabledReason?: string;
  /** A self-serve way out of the disabled state, shown under the reason. */
  action?: JSX.Element;
  recommended?: boolean;
};

/**
 * "שליחה בוואטסאפ" → which pipe? One dialog replacing two bulk buttons (2026-09-30): the realtor
 * used to have to know the difference between "דיוור בתוסף" and "וואטסאפ רשמי" before she had
 * decided to send anything.
 *
 * ⚠️ CAPABILITY IS `useWhatsappNumbers()`, NEVER THE FEATURE FLAG. `advancedFeatures` says the
 * account is LICENSED for official WhatsApp; whether a number is actually connected is a
 * `whatsapp-number-access` row listing this user in `memberMails`. `ListingShareOfficialDialog`
 * already branches on `numbers.length === 0` and hides its send button; this moves that check one
 * dialog earlier, so nobody enters a dialog that cannot send.
 *
 * ⚠️ TEMPLATE APPROVAL IS DELIBERATELY NOT A GATE HERE. `sendable` comes from
 * `POST /whatsapp/listing-share/status`, which is per LISTING and per NUMBER and polls every 15s
 * while Meta reviews. Gating on it would mean a network round-trip before this dialog could render,
 * to produce a state that resolves itself in minutes and that the next dialog already explains with
 * a spinner. So: this chooser gates on ONBOARDING (permanent, needs a human); the dialog behind it
 * gates on APPROVAL (transient, self-healing).
 */
export default function ListingShareWhatsappChooserDialog({
  open,
  onClose,
  onChoose,
  recipientCount,
  hasOfficialFeature,
  extensionDisabledReason,
  lastChannel,
  officialDescription = "נשלח מהמספר העסקי שלכם, אוטומטית ובלי לפתוח שום טאב. הנכס נוסף למיניסייט של כל לקוח."
}: Props): JSX.Element {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { numbers, loading: numbersLoading } = useWhatsappNumbers();
  // Mounted only while this dialog is open (the parent renders it conditionally), so opening the
  // listing modal does not start the singleton's 4s extension poll just for being open.
  const { appStatus: extensionLive, dataReady: extensionProbed } = useWebotStatus();

  const officialConnected = numbers.length > 0;
  const options: Option[] = [
    {
      channel: "official",
      visible: hasOfficialFeature || SHOW_OFFICIAL_WHEN_UNLICENSED,
      title: "וואטסאפ רשמי",
      description: officialDescription,
      icon: <WhatsAppIcon />,
      probing: hasOfficialFeature && numbersLoading,
      recommended: true,
      disabledReason: !hasOfficialFeature
        ? "זמין בחבילת וואטסאפ רשמי. דברו עם התמיכה כדי להפעיל."
        : numbersLoading || officialConnected
        ? undefined
        : "המספר העסקי עדיין לא מחובר לחשבון — פנו לתמיכה כדי להפעיל שליחה רשמית.",
      action:
        hasOfficialFeature && !numbersLoading && !officialConnected ? (
          <Link
            href={PATH_DASHBOARD.general.whatsappOfficial}
            target="_blank"
            rel="noopener"
            onClick={(event) => event.stopPropagation()}
          >
            לחיבור מספר
          </Link>
        ) : undefined
    },
    {
      channel: "extension",
      visible: true,
      title: "שליחה מהוואטסאפ שלי (תוסף)",
      description: "נשלח מהוואטסאפ האישי דרך תוסף הכרום — צריך ש-WhatsApp Web יהיה פתוח בטאב.",
      icon: <ChatRoundedIcon />,
      probing: !extensionProbed,
      // The snapshot reason is the more specific problem, so it wins over the liveness message.
      disabledReason: extensionDisabledReason
        ? extensionDisabledReason
        : !extensionProbed || extensionLive
        ? undefined
        : "לא זיהינו WhatsApp Web פתוח. פתחו אותו בטאב נוסף — נזהה תוך כמה שניות.",
      action:
        extensionProbed && !extensionLive && !extensionDisabledReason ? (
          <Link component={RouterLink} to={PATH_DASHBOARD.whatsAppWeb.status} onClick={(event) => event.stopPropagation()}>
            בדיקת חיבור התוסף
          </Link>
        ) : undefined
    }
  ];

  const visibleOptions = options.filter((option) => option.visible);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" fullScreen={fullScreen}>
      <DialogTitle sx={{ pb: 1 }}>
        שליחה בוואטסאפ
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {`${recipientCount} לקוחות נבחרו. איך לשלוח?`}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pb: 3 }}>
        <Stack spacing={1.5}>
          {visibleOptions.map((option) => {
            const disabled = Boolean(option.disabledReason) || option.probing;
            const isLast = lastChannel === option.channel && !disabled;
            return (
              <Card
                key={option.channel}
                variant="outlined"
                role="button"
                tabIndex={disabled ? -1 : 0}
                aria-disabled={disabled}
                onClick={disabled ? undefined : () => onChoose(option.channel)}
                onKeyDown={(event) => {
                  if (disabled || (event.key !== "Enter" && event.key !== " ")) return;
                  event.preventDefault();
                  onChoose(option.channel);
                }}
                sx={{
                  p: 2,
                  overflow: "visible",
                  cursor: disabled ? "default" : "pointer",
                  borderColor: isLast ? "primary.main" : "divider",
                  opacity: disabled ? 0.72 : 1,
                  transition: "border-color .15s, background-color .15s",
                  ...(disabled ? {} : { "&:hover": { borderColor: "primary.main", bgcolor: "action.hover" } })
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ minWidth: 0 }}>
                  <Box sx={{ color: disabled ? "text.disabled" : "success.dark", display: "flex", flexShrink: 0, mt: 0.25 }}>
                    {option.probing ? <CircularProgress size={22} /> : option.icon}
                  </Box>
                  <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle2" sx={{ minWidth: 0 }}>
                        {option.title}
                      </Typography>
                      {option.recommended && !disabled && (
                        <Label color="success" variant="ghost">
                          מומלץ
                        </Label>
                      )}
                      {isLast && (
                        <Label color="info" variant="ghost">
                          האחרונה שבחרתם
                        </Label>
                      )}
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
                      {option.description}
                    </Typography>
                    {option.probing && (
                      <Typography variant="caption" color="text.disabled">
                        בודקים חיבור…
                      </Typography>
                    )}
                    {!option.probing && option.disabledReason && (
                      <Typography variant="caption" sx={{ color: "warning.dark", overflowWrap: "anywhere" }}>
                        {option.disabledReason}
                      </Typography>
                    )}
                    {!option.probing && option.action && <Typography variant="caption">{option.action}</Typography>}
                  </Stack>
                </Stack>
              </Card>
            );
          })}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
